import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { Loader2, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LeadForm = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent. We’ll be in touch shortly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-2 border-ink bg-cardwhite px-4 py-3 font-mono text-sm text-ink placeholder:text-muted2/60 focus:outline-none focus:border-orange focus:bg-paper transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="bg-orange text-ink halftone-ink">
      <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-14 px-5 py-24 sm:px-8 lg:grid-cols-2 lg:py-32">
        <Reveal>
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-ink/70">/ Contact</p>
          <h2 className="font-bebas text-5xl uppercase leading-[0.85] tracking-tight sm:text-6xl lg:text-7xl">
            Put your crews <br /> on GROUNDWORK.
          </h2>
          <p className="mt-6 max-w-md font-mono text-sm leading-relaxed text-ink/80">
            Running a tour, a launch, or a nationwide sampling program? Tell us about your street team and we’ll
            get you set up with a demo.
          </p>

          <div className="mt-10 space-y-4 border-t-2 border-ink pt-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Email</p>
              <a
                href="mailto:info@groundworkmedia.app"
                data-testid="contact-email"
                className="font-bebas text-3xl tracking-wide underline decoration-2 underline-offset-4"
              >
                info@groundworkmedia.app
              </a>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink/60">Where we operate</p>
              <p className="font-bebas text-3xl tracking-wide">LOS ANGELES · NYC · REMOTE</p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            data-testid="contact-form"
            onSubmit={submit}
            className="border-2 border-ink bg-paper p-6 shadow-brutal sm:p-8"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em]">Name*</label>
                <input
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Jane Rivera"
                  className={inputCls}
                />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em]">Email*</label>
                <input
                  data-testid="contact-email-input"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@brand.com"
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em]">Company</label>
              <input
                data-testid="contact-company-input"
                value={form.company}
                onChange={update("company")}
                placeholder="Brand / Agency"
                className={inputCls}
              />
            </div>
            <div className="mt-4">
              <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em]">Message*</label>
              <textarea
                data-testid="contact-message-input"
                value={form.message}
                onChange={update("message")}
                rows={4}
                placeholder="Tell us about your program, markets, and crew size…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-ink py-4 font-mono text-xs font-bold uppercase tracking-[0.2em] text-paper transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
